import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronLeft, ChevronRight, 
  BarChart2, ArrowUp, Calendar, User 
} from 'lucide-react';

const SuccessStories = () => {
  // Simulated case studies data
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Simulating API call to Sanity.io
    setTimeout(() => {
      setCaseStudies([
        {
          id: 1,
          sellerName: "Sarah K., Elementary Math Resources",
          slug: "sarah-math-resources",
          imageUrl: "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=800&q=80",
          quote: "I tripled my monthly earnings after optimizing just 10 listings with Mr. Joel's Tools. The SEO analysis showed me exactly what I was missing.",
          challenge: "Struggling to get visibility for my math resources despite quality content. My listings weren't ranking well, and I was spending hours trying to write effective descriptions.",
          results: ["320%", "increase in sales", "15 → 4", "average rank position", "200%", "more store traffic", "5 hrs", "saved weekly"]
        },
        {
          id: 2,
          sellerName: "Mark T., High School History",
          slug: "mark-history",
          imageUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=800&q=80",
          quote: "The competitor analysis feature was a game-changer. I went from page 5 to page 1 for my main keywords in just two weeks!",
          challenge: "My US History bundles weren't getting the visibility they deserved. I knew my resources were good, but I couldn't compete with top sellers in my niche.",
          results: ["150%", "more product views", "Page 5 → Page 1", "search ranking", "85%", "conversion increase", "3 hrs", "saved weekly"]
        },
        {
          id: 3,
          sellerName: "Jessica L., Special Education Resources",
          slug: "jessica-special-ed",
          imageUrl: "https://images.unsplash.com/photo-1544725176-7c40e5a71c5e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&h=800&q=80",
          quote: "I used to dread writing product descriptions. Now I generate SEO-optimized descriptions in minutes that actually convert!",
          challenge: "As a special education teacher creating unique resources, I struggled to communicate their value effectively. My descriptions were either too technical or too vague.",
          results: ["180%", "sales increase", "40%", "higher conversion rate", "8 hrs", "saved weekly", "27", "optimized listings"]
        }
      ]);
      setLoading(false);
    }, 1500);
  }, []);

  // Dropdown state for resources menu
  const [dropdownOpen, setDropdownOpen] = useState(false);

  return (
    <div className="bg-slate-50 text-slate-900 min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-sky-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">J</div>
                <span className="font-bold text-lg text-slate-800">Mr Joel's TPT Tools</span>
              </a>
            </div>
            
            <nav className="hidden md:flex md:items-center md:space-x-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-sky-600">Features</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-sky-600">Pricing</a>
              <a href="#how-to-use" className="text-sm font-medium text-slate-600 hover:text-sky-600">How to Use</a>
              
              <div className="relative">
                <button 
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-1 text-sm font-medium text-slate-600 hover:text-sky-600"
                >
                  <span>Resources</span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div 
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-lg shadow-lg p-2 z-50"
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <a href="/blog" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-sky-600 rounded">Seller's Blog</a>
                    <a href="/case-studies" className="block px-4 py-2 text-sm font-bold text-sky-600 bg-slate-100 rounded">Success Stories</a>
                    <a href="/seo-tips" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-sky-600 rounded">SEO Tips</a>
                    <a href="/seller-tips" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 hover:text-sky-600 rounded">Seller Tips</a>
                  </div>
                )}
              </div>
            </nav>
            
            <div className="flex items-center space-x-2">
              <a href="/app" className="text-xs sm:text-sm font-semibold py-2 px-2 sm:px-3 rounded-lg border-2 border-sky-500 text-sky-600 hover:bg-sky-100/50 transition-colors whitespace-nowrap">Launch App</a>
              <a href="/app" className="text-xs sm:text-sm font-semibold py-2 px-2 sm:px-3 rounded-lg border-2 border-sky-500 text-sky-600 hover:bg-sky-100/50 transition-colors whitespace-nowrap">Free Trial</a>
              <a href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689?utm_source=My%20site&utm_campaign=Mr%20Joel%20Site%20Landing%20Page" 
                 className="text-xs sm:text-sm bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg shadow-md transition-transform transform hover:scale-105 whitespace-nowrap">
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-sky-800 tracking-tight">
              Success Stories
            </h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">
              See the real-world results from TPT sellers who use our app to save time, improve SEO, and grow their stores.
            </p>
          </div>

          {loading ? (
            <div className="mt-16 text-center">
              <div className="inline-flex items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500 mr-4"></div>
                <p className="text-slate-500">Loading success stories...</p>
              </div>
            </div>
          ) : (
            <div className="mt-16 space-y-16">
              {caseStudies.map(study => (
                <div key={study.id} className="grid md:grid-cols-2 gap-8 md:gap-12 items-center bg-white p-6 sm:p-8 rounded-2xl shadow-xl border border-slate-200">
                  <div className="aspect-square bg-slate-100 rounded-lg overflow-hidden shadow-md">
                    <img 
                      src={study.imageUrl} 
                      alt={study.sellerName} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div>
                    <blockquote className="text-xl italic font-medium text-slate-800 border-l-4 border-sky-500 pl-4 py-1">
                      "{study.quote}"
                    </blockquote>
                    <p className="mt-4 font-bold text-slate-700 text-right">- {study.sellerName}</p>
                    
                    <div className="mt-8">
                      <h4 className="flex items-center font-bold text-slate-700">
                        <BarChart2 className="h-5 w-5 mr-2 text-sky-600" />
                        The Challenge:
                      </h4>
                      <p className="mt-2 text-slate-600">{study.challenge}</p>
                    </div>
                    
                    <div className="mt-8">
                      <h4 className="flex items-center font-bold text-slate-700">
                        <ArrowUp className="h-5 w-5 mr-2 text-green-600" />
                        The Results:
                      </h4>
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {study.results.map((result, index) => (
                          <div key={index} className="text-center bg-slate-50 p-3 rounded-lg border border-slate-200">
                            <p className="text-xl font-bold text-sky-600">{result}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <a 
                      href={`/case-study/${study.slug}`}
                      className="mt-8 inline-flex items-center bg-sky-600 hover:bg-sky-700 text-white font-semibold py-2.5 px-5 rounded-lg text-sm shadow-md transition-colors"
                    >
                      Read Full Story
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="mt-24 bg-slate-800 text-slate-400 py-12">
        <div className="max-w-5xl mx-auto text-center px-4">
          <p>&copy; 2025 Mr. Joel's TPT Tools. All Rights Reserved.</p>
          <div className="mt-4 space-x-4">
            <a href="/contact" className="hover:text-white">Contact</a>
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SuccessStories;